import {Component, OnDestroy, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {NotificationsService, ProjectService} from '@catcore/services';
import {Project} from '@catcore/types/api-v2';
import {ProjectPropertiesService} from '@catdetails/services/project-properties.service';
import {isNullOrUndefined} from 'util';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ConfirmationService} from "primeng/primeng";
import {DomSanitizer} from "@angular/platform-browser";
import {Subscription} from "rxjs";
import {GgwProfileService} from "@catmodels/ggw-profiles/services/ggw-profile.service";
import {ROLE, UserService} from "@catcore/services/user.service";
import {EvidRevision} from "@catmodels/evid/types";
import {EvidRevisionService} from "@catmodels/evid/services/evid-revision.service";
import {ProgramService} from "@catmodels/programs/services/program.service";
import {Program} from "@catmodels/programs/types";

/**
 * Component to manage the basic property values for any project model
 */
@Component({
  templateUrl: './project-properties.component.html',
})
export class ProjectPropertiesComponent
  implements OnInit, OnDestroy {

  private subscriptions: any;
  private ggwZoneStrategyOptionsSubsc: Subscription;
  private ggwStrategyOptionsSubsc: Subscription;
  private ggwRouteStrategyOptionsSubsc: Subscription;
  private programSubsc: Subscription;
  private evidRevisionOptionsSubsc: Subscription;

  ggwZoneStrategyOptions: any;
  ggwProfileStrategyOptions: any;
  ggwRouteStrategyOptions: any;
  programOptions: any;
  evidRevisionOptions: any;

  project = null;
  form: FormGroup;

  projectInitialized = false;
  ggwZoneStrategyOptionsInitialized = false;
  ggwProfileStrategyOptionsInitialized = false;
  ggwRouteStrategyOptionsInitialized = false;
  evidRevisionOptionsInitialized = false;

  ROLES = ROLE;

  get initialized() {
    return this.projectInitialized
      && this.ggwZoneStrategyOptionsInitialized
      && this.ggwProfileStrategyOptionsInitialized
      && this.ggwRouteStrategyOptionsInitialized
      && this.evidRevisionOptionsInitialized;
  }

  get lopaImgBase64() {
    if (this.project && this.project.lopa) {
      return this.sanitizer.bypassSecurityTrustUrl(this.project.lopa);
    }
    return null;
  }

  constructor(private projectService: ProjectService,
              private router: Router,
              private projectPropertiesService: ProjectPropertiesService,
              private notificationService: NotificationsService,
              private confirmationService: ConfirmationService,
              private ggwProfileService: GgwProfileService,
              private evidRevisionService: EvidRevisionService,
              private sanitizer: DomSanitizer,
              private userService: UserService,
              private programService: ProgramService) {
    this.form = new FormGroup({
      program: new FormControl(null, []),
      number: new FormControl(0, [Validators.required]),
      name: new FormControl('', [Validators.required]),
      watermark_text: new FormControl(null, []),
      end_customer: new FormControl(null, []),
      completion_center: new FormControl(null, []),
      tail_sign: new FormControl(null, []),
      aircraft_type: new FormControl('', [Validators.required]),
      gui_aircraft_type: new FormControl('', []),
      aircraft_msn: new FormControl('', [Validators.required]),
      lopa: new FormControl(null, []),
      lopa_name: new FormControl(null, []),
      drm_domain: new FormControl(null, []),
      drm_init_sh: new FormControl(null, []),
      drm_init_sh_name: new FormControl(null, []),
      audio_buffer_latency: new FormControl(150, [Validators.required]),
      ggw_zone_strategy_name: new FormControl(null, [Validators.required]),
      ggw_profile_strategy_name: new FormControl(null, [Validators.required]),
      ggw_route_strategy_name: new FormControl(null, [Validators.required]),
      ggw_system_function_level: new FormControl(null, []),
      evid_revision: new FormControl(null, []),
    });
  }

  ngOnInit() {
    this.subscriptions =
      this.projectService
        .getCurrentProject()
        .delay(100)
        .subscribe((project: Project) => {
          if (isNullOrUndefined(project) || isNullOrUndefined(project.id)) {
            this.project = null;
            this.projectPropertiesService.setInActive();
          } else {
            this.project = project;
            this.projectPropertiesService.setActive();
            this.form.patchValue(this.project);
            this.form.patchValue({program: this.project._links.program.href});
            if (this.project._links.hasOwnProperty('evid_revision')) {
              this.form.patchValue({
                evid_revision: this.project._links.evid_revision.href
              });
            } else {
              this.form.patchValue({
                evid_revision: null
              });
            }
          }
          this.projectInitialized = true;
        });

    this.ggwZoneStrategyOptionsSubsc = this.projectService
      .getGgwZoneStrategyOptions()
      .subscribe((options) => {
        this.ggwZoneStrategyOptions = options;
        this.ggwZoneStrategyOptionsInitialized = true;
      });

    this.ggwStrategyOptionsSubsc = this.projectService
      .getGgwProfileStrategyOptions()
      .subscribe((options) => {
        this.ggwProfileStrategyOptions = options;
        this.ggwProfileStrategyOptionsInitialized = true;
      });

    this.ggwRouteStrategyOptionsSubsc = this.projectService
      .getGgwRouteStrategyOptions()
      .subscribe((options) => {
        this.ggwRouteStrategyOptions = options;
        this.ggwRouteStrategyOptionsInitialized = true;
      });

    this.programSubsc = this.programService
      .getAll()
      .subscribe((programs) => {
        this.programOptions = [];
        programs.forEach((program) => {
          this.programOptions.push({
            value: program._links.self.href,
            label: program.name
          });
        });
      });

    this.evidRevisionOptionsSubsc = this.evidRevisionService
      .getAll()
      .subscribe((evidRevisions) => {
        this.evidRevisionOptions = [{
          value: null,
          label: '',
        }];
        let latest: EvidRevision;
        evidRevisions.forEach((evidRevision) => {
          if (evidRevision.name.toLowerCase() === 'latest') {
            latest = evidRevision;
          } else {
            this.evidRevisionOptions.push({
              value: evidRevision._links.self.href,
              label: evidRevision.label,
            });
          }
        });
        if (latest) {
          this.evidRevisionOptions.push({
            value: latest._links.self.href,
            label: latest.label,
          });
        }
        this.evidRevisionOptionsInitialized = true;
      });
  }

  /**
   * read the provided file-stream and set it as the to be used icon file
   * @param $event
   */
  updateLopaFile($event) {
    this.updateFile($event, (name: string, value: string) => {
      this.form.patchValue({lopa_name: name});
      this.form.patchValue({lopa: value});
      this.form.get('lopa').markAsDirty();
    });
  }

  updateDrmInitShFile($event) {
    this.updateFile($event, (name: string, value: string) => {
      this.form.patchValue({drm_init_sh_name: name});
      this.form.patchValue({drm_init_sh: value});
      this.form.get('drm_init_sh').markAsDirty();
    });
  }

  private updateFile($event, onFinish: (name: string, value: string) => void) {
    const fileReader = new FileReader();
    const fileInfo = $event.target.files[0];
    const fileName = fileInfo.name;
    fileReader.onload = (fileEvent: any) => {
      const uploadTarget: any = fileEvent.target;
      const fileContent = `${encodeURI(uploadTarget.result)}`;
      onFinish(fileName, fileContent);
    };
    fileReader.readAsDataURL(fileInfo);
  }

  ngOnDestroy() {
    this.programSubsc.unsubscribe();
    this.ggwZoneStrategyOptionsSubsc.unsubscribe();
    this.ggwRouteStrategyOptionsSubsc.unsubscribe();
    this.ggwStrategyOptionsSubsc.unsubscribe();
    this.projectPropertiesService.setInActive();
    this.subscriptions.unsubscribe();
  }

  onSubmit(event) {
    if (this.project !== null) {
      const formValue = this.form.value;
      if (this.form.get('lopa').pristine) {
        delete formValue['lopa'];
      }
      if (this.form.get('drm_init_sh').pristine) {
        delete formValue['drm_init_sh'];
      }
      const subscription = this.projectService.update({id: this.project.id, ...formValue})
        .finally(() => subscription.unsubscribe())
        .subscribe((result) => {
          this.notificationService.success('Update successful', "The project has been updated successfully.");
          this.projectService.setCurrentProject(result);
          this.form.markAsPristine();
        });
    } else {
      const subscription = this.projectService.add(this.form.value)
        .finally(() => subscription.unsubscribe())
        .subscribe((result) => {
          this.notificationService.success('Project creation successful', "The project has been created successfully.");
          this.projectService.refreshList();
          this.projectService.setCurrentProject(result);
          this.router.navigate(['projects', result.id, 'properties']);
        });
    }
  }

  onDelete(event) {
    if (this.project !== null) {
      this.confirmationService.confirm({
        message: 'Do you really want to remove this Project?',
        accept: () => {
          this
            .projectService
            .delete(this.project)
            .subscribe(() => {
              this.projectService.resetCurrentProject();
              this.projectService.refreshList();
              this.router.navigate(['projects']);
            });
        },
      });
    }
  }

  onClone(event) {
      this.confirmationService.confirm({
          message: 'Do you really want to clone this project?',
          accept: () => {

              const subscription = this.projectService.clone(this.project)
                  .finally(() => subscription.unsubscribe())
                  .subscribe((result) => {
                      this.notificationService.success('Project cloned', 'The project was successfully cloned.');
                      this.projectService.refreshList();
                      this.projectService.setCurrentProject(result);
                      this.router.navigate(['projects', result.id, 'properties']);
                  });
          },
      });
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_master@2/app/Resources/Public/src/app/modules/project-details/components/project-properties/project-properties.component.ts